import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — lubricants are in the ESPR working plan 2025-2030
 * and carry CLP hazard-labelling; the EU Ecolabel for lubricants (Decision
 * 2018/1702) sets biodegradability + ecotoxicity criteria the DPP surfaces.
 * Chemical-formulation family (see detergent-v1); the identity + hazards + SDS
 * shape is shared, the accents are base-oil type/viscosity and waste-oil
 * (re-refining) circularity. Field ids are STABLE; trued up on the delegated act.
 */
export const lubricantV1: PassportSchemaDef = {
  id: "lubricant-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Yağlayıcı Ürün Pasaportu",
    en: "Lubricant Product Passport",
    de: "Schmierstoff-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/lubricant/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.lubricantType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "engine-oil",
            "gear-oil",
            "hydraulic-fluid",
            "grease",
            "metalworking-fluid",
            "turbine-oil",
            "industrial",
            "other",
          ],
          labels: { tr: "Yağlayıcı türü", en: "Lubricant type", de: "Schmierstofftyp" },
        },
        {
          id: "identity.baseOilType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["mineral", "synthetic", "semi-synthetic", "bio-based", "re-refined", "other"],
          labels: { tr: "Baz yağ türü", en: "Base oil type", de: "Grundöltyp" },
        },
        {
          id: "identity.viscosityGrade",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Viskozite sınıfı", en: "Viscosity grade", de: "Viskositätsklasse" },
          description: "E.g. SAE 5W-30, ISO VG 46.",
          highlight: true,
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.netContent",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Net içerik", en: "Net content", de: "Nettoinhalt" },
          description: "E.g. 4 L, 205 L drum.",
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
      ],
    },
    {
      id: "hazards",
      labels: {
        tr: "Tehlike Sınıflandırması (CLP)",
        en: "Hazard Classification (CLP)",
        de: "Gefahreneinstufung (CLP)",
      },
      fields: [
        {
          id: "hazards.clpSignalWord",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["danger", "warning", "none"],
          labels: { tr: "CLP uyarı kelimesi", en: "CLP signal word", de: "CLP-Signalwort" },
          highlight: true,
        },
        {
          id: "hazards.hazardStatements",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Tehlike ifadeleri (H)", en: "Hazard statements (H)", de: "Gefahrenhinweise (H)" },
          description: "H-codes, e.g. H304, H411.",
        },
        {
          id: "hazards.precautionaryStatements",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Önlem ifadeleri (P)",
            en: "Precautionary statements (P)",
            de: "Sicherheitshinweise (P)",
          },
        },
        {
          id: "hazards.ghsPictograms",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "GHS piktogramları", en: "GHS pictograms", de: "GHS-Piktogramme" },
        },
        {
          id: "hazards.aquaticHazard",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Su ortamı için tehlikeli", en: "Aquatic hazard", de: "Gewässergefährdend" },
        },
      ],
    },
    {
      id: "composition",
      labels: { tr: "Bileşim", en: "Composition", de: "Zusammensetzung" },
      fields: [
        {
          id: "composition.baseOilContentPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: { tr: "Baz yağ içeriği", en: "Base oil content", de: "Grundölanteil" },
        },
        {
          id: "composition.additivesSummary",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Katkı maddeleri", en: "Additives", de: "Additive" },
        },
        {
          id: "composition.bioBasedContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Biyo-bazlı içerik", en: "Bio-based content", de: "Biobasierter Anteil" },
          highlight: true,
        },
        {
          id: "composition.reRefinedContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Yeniden rafine edilmiş içerik",
            en: "Re-refined content",
            de: "Anteil aufbereiteten Öls",
          },
          highlight: true,
        },
        {
          id: "composition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
        },
        {
          id: "composition.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
        },
      ],
    },
    {
      id: "performance",
      labels: { tr: "Performans", en: "Performance", de: "Leistung" },
      fields: [
        {
          id: "performance.performanceSpecs",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Performans spesifikasyonları",
            en: "Performance specifications",
            de: "Leistungsspezifikationen",
          },
          description: "E.g. API SP, ACEA C3, ISO VG classes.",
        },
        {
          id: "performance.viscosityIndex",
          type: "number",
          required: false,
          tier: "public",
          labels: { tr: "Viskozite indeksi", en: "Viscosity index", de: "Viskositätsindex" },
        },
        {
          id: "performance.operatingTempRange",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Çalışma sıcaklık aralığı",
            en: "Operating temperature range",
            de: "Betriebstemperaturbereich",
          },
        },
        {
          id: "performance.flashPointC",
          type: "number",
          required: false,
          tier: "public",
          unit: "°C",
          labels: { tr: "Parlama noktası", en: "Flash point", de: "Flammpunkt" },
        },
        {
          id: "performance.standardConformity",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Standart uygunluğu", en: "Standard conformity", de: "Normkonformität" },
        },
      ],
    },
    {
      id: "environment",
      labels: { tr: "Çevre ve Bertaraf", en: "Environment & Disposal", de: "Umwelt & Entsorgung" },
      fields: [
        {
          id: "environment.biodegradabilityPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: { tr: "Biyobozunurluk oranı", en: "Biodegradability", de: "Biologische Abbaubarkeit" },
          description: "Ready biodegradability (OECD 301) — EU Ecolabel lubricants criterion.",
          highlight: true,
        },
        {
          id: "environment.ecolabelType",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["eu-ecolabel", "other", "none"],
          labels: { tr: "Ekolojik etiket", en: "Ecolabel", de: "Umweltzeichen" },
        },
        {
          id: "environment.ecotoxicity",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ekotoksisite", en: "Ecotoxicity", de: "Ökotoxizität" },
        },
        {
          id: "environment.disposalInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bertaraf talimatları", en: "Disposal instructions", de: "Entsorgungshinweise" },
        },
        {
          id: "environment.wasteOilCollectionInfo",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Atık yağ toplama bilgisi",
            en: "Waste oil collection info",
            de: "Altöl-Sammelinformationen",
          },
        },
        {
          id: "environment.sdsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Güvenlik Bilgi Formu (SDS)",
            en: "Safety Data Sheet (SDS)",
            de: "Sicherheitsdatenblatt (SDB)",
          },
        },
      ],
    },
  ],
};
